/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
*/
import { Component, inject, OnDestroy, Type } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AppComponent } from '@src/app/app.component';
import { User } from '@src/app/model/user';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { AboChart } from '@src/app/modules/agent/agent-dashboard-page/AboChart';
import { AbuChart } from '@src/app/modules/agent/agent-dashboard-page/AbuChart';
import { MoaAllChart } from '@src/app/modules/agent/agent-dashboard-page/MoaAllChart';
import { MoaChart } from '@src/app/modules/agent/agent-dashboard-page/MoaChart';
import { MoaOmcChart } from '@src/app/modules/agent/agent-dashboard-page/MoaOmcChart';
import { RboChart } from '@src/app/modules/agent/agent-dashboard-page/RboChart';
import { RbuChart } from '@src/app/modules/agent/agent-dashboard-page/RbuChart';
import { SraAllChart } from '@src/app/modules/agent/agent-dashboard-page/SraAllChart';
import { SraChart } from '@src/app/modules/agent/agent-dashboard-page/SraChart';
import { SraMyOmcChart } from '@src/app/modules/agent/agent-dashboard-page/SraMyOmcChart';
import { RboMyOmcChart } from '@src/app/modules/agent/agent-dashboard-page/RboMyOmcChart';
import { RboMyRequestsChart } from '@src/app/modules/agent/agent-dashboard-page/RboMyRequestsChart';
import { AboMyOmcChart } from '@src/app/modules/agent/agent-dashboard-page/AboMyOmcChart';
import { AboMyRequestsChart } from '@src/app/modules/agent/agent-dashboard-page/AboMyRequestsChart';
import { RbuMyOmcChart } from '@src/app/modules/agent/agent-dashboard-page/RbuMyOmcChart';
import { AbuMyOmcChart } from '@src/app/modules/agent/agent-dashboard-page/AbuMyOmcChart';
import { UnreadChart } from '@src/app/modules/agent/agent-dashboard-page/UnreadChart';
import { UnreadActionsChart } from '@src/app/modules/agent/agent-dashboard-page/UnreadActionsChart';
import { OverdueRequestsChart } from '@src/app/modules/agent/agent-dashboard-page/OverdueRequestsChart';
import { OverdueActionsChart } from '@src/app/modules/agent/agent-dashboard-page/OverdueActionsChart';
import { AvailableWidget, ChosenWidget, IWidget } from '@src/app/shared/dashboard/DashboardTypes';
import { DashboardComponent } from '@src/app/shared/dashboard/dashboard.component';

@Component({
    selector: 'app-agent-dashboard-page',
    templateUrl: './agent-dashboard-page.component.html',
    styleUrls: ['./agent-dashboard-page.component.scss'],
    imports: [DashboardComponent]
})
export class AgentDashboardPageComponent implements OnDestroy {

    currentUser: User;
    currentUserSvc = inject(CurrentUserService);

    dashboardName = 'agentDashboard';
    allWidgets: AvailableWidget[] = [];
    defaultWidgets : ChosenWidget[] = [
        { code: 'unreadActions', widthWeight: 1, heightWeight: 3 }, 
        { code: 'moaAll', widthWeight: 2, heightWeight: 3}, 
        { code: 'abu', widthWeight: 2, heightWeight: 3 }, 
        { code: 'abo', widthWeight: 2, heightWeight: 3 }, 
        { code: 'overdueActions', widthWeight: 1, heightWeight: 3 },
        { code: 'unread', widthWeight: 1, heightWeight: 3 }, 
        { code: 'sraAll', widthWeight: 2, heightWeight: 3 }, 
        { code: 'rbu', widthWeight: 2, heightWeight: 3 }, 
        { code: 'rbo', widthWeight: 2, heightWeight: 3 }, 
        { code: 'overdueRequests', widthWeight: 1, heightWeight: 3 }
    ];

    subscriptions: Subscription[] = [];

    constructor() {
        const titleSvc = inject(Title);

        titleSvc.setTitle($localize`${AppComponent.appName} - Agent Dashboard`)
        this.subscriptions.push(this.currentUserSvc.getCurrentUser().subscribe(user => {
            this.currentUser = user;
            this.loadCharts();
        }));
    }

    available(code: string, name: string, maker: () => DashboardChart) {
        this.allWidgets.push({ code, name, chart: maker(), maker });
    }

    pushWidget(code: string, name: string, widgetClass: Type<IWidget>) {
        this.allWidgets.push({ code, name, widgetClass, chart: null });
    }

    loadCharts() {
        this.allWidgets.length = 0;
        
        this.pushWidget('moa', 'My Open Actions', MoaChart);
        this.pushWidget('moaOmc', 'My OMC Open Actions', MoaOmcChart);
        this.pushWidget('moaAll', 'All Open Actions', MoaAllChart);

        this.pushWidget('unread', 'My Unread Requests', UnreadChart);
        this.pushWidget('unreadActions', 'My Unread Actions', UnreadActionsChart);

        this.pushWidget('overdueRequests', 'My Overdue Requests', OverdueRequestsChart);
        this.pushWidget('overdueActions', 'My Overdue Actions', OverdueActionsChart);

        this.pushWidget('sra', 'My Service Requests', SraChart);
        this.pushWidget('sraOmc', 'My OMC Open Service Requests', SraMyOmcChart);
        this.pushWidget('sraAll', 'All Open Service Requests', SraAllChart);

        this.pushWidget('abu', 'Action Creation By User', AbuChart);
        this.pushWidget('abuMyOmc', 'Action Creation By User For My OMCs', AbuMyOmcChart);

        this.pushWidget('abo', 'Top OMCs By Action', AboChart);
        this.pushWidget('aboMyOmc', 'My Top OMCs By Action', AboMyOmcChart);
        this.pushWidget('aboMyRequests', 'My Actions Top OMCs', AboMyRequestsChart);

        this.pushWidget('rbu', 'Request Distribution', RbuChart);
        this.pushWidget('rbuMyOmcs', 'Requests Distribution for My OMCs', RbuMyOmcChart);

        this.pushWidget('rbo', 'Busiest OMCs by Requests', RboChart);
        this.pushWidget('rboMyOmc', 'My Busiest OMCs by Requests', RboMyOmcChart);
        this.pushWidget('rboMyRequests', 'My Requests Busiest OMCs', RboMyRequestsChart);
    }

    ngOnDestroy(): void {
        this.allWidgets.forEach ( aw => aw.chart = null);
        this.subscriptions.forEach(s => s.unsubscribe());
    }
    
    canDeactivate() {
        return true;
    }
}
